import { Injectable } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { MarketingDataAsset, MarketingDataExtraLinks } from 'common/models/product';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface FeaturedMedia {
    src: string;
    src2x: string;
    type: 'image'|'video'|'embed';
}

export interface MarketingAsset {
    title: string;
    description: string;
    cta?: {
        text: string;
        url: SafeUrl;
    };
    assetLink?: string;
    media: FeaturedMedia;
    additionalLinks?: {
        link: SafeUrl | '';
        image: string;
        alt: string;
    }[];
}

export interface MarketingSection {
    title: string;
    description?: string;
    items: MarketingAsset[];
    sectionSingleCTA?: {
        text: string;
        url: string;
    };
}

@Injectable({ providedIn: 'root' })
export class MarketingProductService {
    constructor(
        private readonly translate: TranslateService,
        private readonly sanitize: DomSanitizer
    ) {}

    sanitizeUrl(url: string) {
        return url ? this.sanitize.bypassSecurityTrustUrl(url) : '';
    }

    buildExtras(extraAssets: {[key: string]: MarketingDataExtraLinks}) {
        return Object.keys(extraAssets).map((asset) => (
            {
                link: extraAssets[asset].LINK,
                image: extraAssets[asset].IMAGE,
                alt: extraAssets[asset].ALT
            }
        ));
    }

    buildAssets(assets: {[key: string]: MarketingDataAsset}): MarketingAsset[] {
        return Object.keys(assets).map((asset: string) => {
            const data = {
                title: assets[asset].TITLE,
                assetLink: assets[asset].ASSET_LINK,
                description: assets[asset].DESCRIPTION,
                cta: assets[asset].CTA && {
                    text: assets[asset].CTA.CTA_TEXT,
                    url: assets[asset].CTA.CTA_URL
                },
                media: {
                    src: assets[asset].MEDIA_SOURCE,
                    src2x: assets[asset].MEDIA_SOURCE_2X,
                    type: assets[asset].MEDIA_TYPE
                },
                additionalLinks: assets[asset].ADDITIONAL_LINKS && this.buildExtras(assets[asset].ADDITIONAL_LINKS)
            };

            return data;
        });
    }

    getMarketingContent(section: string): Observable<MarketingSection> {
        return this.translate.get(section).pipe(
            map((sectionData) => {
                const data = {
                    title: sectionData.TITLE,
                    description: sectionData.DESCRIPTION,
                    items: this.buildAssets(sectionData.ITEMS),
                    sectionSingleCTA: {
                        url: sectionData.SECTION_LINK_URL,
                        text: sectionData.SECTION_LINK
                    }
                };

                return data;
            })
        );
    }
}
